import { useState } from 'react';
import { Save, Trash2, Eye, EyeOff, Key, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import type { Settings } from '../types';
import { CollectClient } from '../lib/api';
import { saveSettings, clearSettings, customServerOf, selfHostedServerOffered, serverUrlFromInput } from '../lib/settings';
import { AdvancedServer } from './AdvancedServer';

/**
 * Settings: the access key and, under "Advanced", an optional self-hosted HIVE
 * server. By default Collect talks to its own server on this site (serverUrl
 * SAME_ORIGIN, lib/settings.ts), so there is no server address to fill in; the
 * Advanced field exists only in builds that can reach another server
 * (selfHostedServerOffered: not the hosted site).
 */
export function SettingsPanel({ initial, onSaved }: { initial: Settings; onSaved: (s: Settings) => void }) {
  const [offerServer] = useState(() => selfHostedServerOffered(initial));
  const [customServer, setCustomServer] = useState(() => customServerOf(initial));
  const [advancedOpen, setAdvancedOpen] = useState(() => Boolean(customServerOf(initial)));
  const [apiKey, setApiKey] = useState(initial.apiKey);
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  /** The form as settings, or null (with the reason shown) when the server address is not usable. */
  const current = (): Settings | null => {
    const server = serverUrlFromInput(customServer);
    if (!server.ok) {
      setAdvancedOpen(true);
      setTestResult({ ok: false, message: server.message });
      return null;
    }
    return { serverUrl: server.serverUrl, apiKey: apiKey.trim() };
  };

  const test = async () => {
    const s = current();
    if (!s) return;
    setTesting(true);
    setTestResult(null);
    // public health first, then an authenticated list; each failure gets its own message
    try {
      const r = await new CollectClient(s).testConnection('settings');
      setTestResult({ ok: r.ok, message: r.message });
    } finally {
      setTesting(false);
    }
  };

  const save = () => {
    const s = current();
    if (!s) return;
    setCustomServer(customServerOf(s));
    saveSettings(s);
    onSaved(s);
  };

  const clear = () => {
    clearSettings();
    setCustomServer('');
    setAdvancedOpen(false);
    setApiKey('');
    setTestResult(null);
    onSaved({ serverUrl: '', apiKey: '' });
  };

  return (
    <div className="max-w-2xl">
      {/* Hero */}
      <div className="mb-10">
        <h1 className="display text-5xl mb-3">Settings</h1>
        <p className="text-base" style={{ color: 'rgba(240,253,244,0.6)' }}>
          {offerServer
            ? 'Your access key, and your own HIVE server if you run one. Saved in this browser only.'
            : 'Your access key. Saved in this browser only.'}
        </p>
      </div>

      {/* Access key */}
      <div className="glass-panel rounded-xl p-6 mb-4">
        <label className="label flex items-center gap-2 mb-3" htmlFor="settings-key">
          <Key size={12} />
          Access key
        </label>
        <div className="relative">
          <input
            id="settings-key"
            className="field pr-12"
            type={showKey ? 'text' : 'password'}
            placeholder="paste your access key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            spellCheck={false}
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
            title={showKey ? 'Hide key' : 'Show key'}
          >
            {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <p className="text-xs mt-2" style={{ color: 'rgba(240,253,244,0.55)' }}>
          The access key Qodoonect gave you. It is kept in this browser and sent only to the Collect server.
        </p>
      </div>

      {/* Self-hosted server (collapsed, empty by default; not on the hosted site) */}
      {offerServer && (
        <div className="glass-panel rounded-xl p-6 mb-4">
          <AdvancedServer
            id="settings-server"
            value={customServer}
            onChange={setCustomServer}
            open={advancedOpen}
            onOpenChange={setAdvancedOpen}
          />
        </div>
      )}

      {/* Test result */}
      {testResult && (
        <div className="glass-panel rounded-xl p-4 mb-4 flex items-start gap-3" role="status" style={{
          borderColor: testResult.ok ? 'rgba(16,185,129,0.4)' : 'rgba(245,158,11,0.4)',
        }}>
          {testResult.ok
            ? <CheckCircle2 size={18} style={{ color: '#34D399' }} className="flex-none mt-0.5" />
            : <AlertCircle size={18} style={{ color: '#FBBF24' }} className="flex-none mt-0.5" />}
          <div className="text-sm" style={{ color: testResult.ok ? '#A7F3D0' : '#FDE68A' }}>{testResult.message}</div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 mb-8">
        <button className="btn btn-primary" onClick={save}>
          <Save size={14} />
          Save settings
        </button>
        <button className="btn btn-ghost" onClick={test} disabled={testing}>
          {testing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          {testing ? 'Testing…' : 'Test connection'}
        </button>
        <div className="flex-1" />
        <button className="btn btn-ghost" onClick={clear} style={{ color: '#F87171', borderColor: 'rgba(239,68,68,0.3)' }}>
          <Trash2 size={14} />
          Clear
        </button>
      </div>

      {/* Help */}
      <div className="glass-panel rounded-xl p-6">
        <h3 className="font-semibold mb-3">How to get an access key</h3>
        <ol className="text-sm space-y-3" style={{ color: 'rgba(240,253,244,0.65)' }}>
          <li className="flex gap-3">
            <span className="font-mono flex-none" style={{ color: '#34D399' }}>1.</span>
            <span>Collect is in closed beta: ask your Qodoonect contact for an access key.</span>
          </li>
          <li className="flex gap-3">
            <span className="font-mono flex-none" style={{ color: '#34D399' }}>2.</span>
            <span>Paste it above and click <i>Test connection</i>.</span>
          </li>
          <li className="flex gap-3">
            <span className="font-mono flex-none" style={{ color: '#34D399' }}>3.</span>
            <span>Click <i>Save settings</i>. Your capsules appear under <i>Capsules</i>.</span>
          </li>
        </ol>
      </div>
    </div>
  );
}
